/* global axios */
import ApiClient from './ApiClient';

// Chat interno (#1748). A API vive FORA do escopo de conta (`/api/v1/internal_chat`), como
// `/api/v1/profile`: a conta vai em `account_id` em TODA chamada, e é ela que o servidor usa
// para recortar o que se vê. Nunca deduzida da URL do navegador — a visão consolidada (#1752) lê
// mais de uma conta da mesma tela, e quem escolhe a conta de cada chamada é o store (a conta do
// CANAL, não a da URL).
class InternalChatAPI extends ApiClient {
  constructor() {
    super('internal_chat', { accountScoped: false });
  }

  getChannels(accountId) {
    return axios.get(`${this.url}/channels`, {
      params: { account_id: accountId },
    });
  }

  // Visão consolidada (#1752): as contas em que a pessoa alcança o chat, com as não lidas e as
  // menções de cada uma. É o único pedido do módulo SEM `account_id` — é a lista dela.
  getAccounts() {
    return axios.get(`${this.url}/accounts`);
  }

  // As pessoas com quem se pode conversar numa conta (#1752), para as contas que não são a da
  // URL — a conta da tela usa o store `agents`, que tem a presença ao vivo.
  getPeople(accountId) {
    return axios.get(`${this.url}/users`, {
      params: { account_id: accountId },
    });
  }

  // Antes de criar contato/conversa para um telefone ou e-mail (#1791): em quais contas de quem
  // pede ele já existe, e se já há conversa. `accountId` é a conta da URL — onde se vai criar.
  getContactMatches(accountId, { type, value }) {
    return axios.get(`${this.url}/contact_matches`, {
      params: { account_id: accountId, type, value },
    });
  }

  openDirectChannel(accountId, userId) {
    return axios.post(`${this.url}/channels`, {
      account_id: accountId,
      user_id: userId,
    });
  }

  getMessages(accountId, channelId, { before, after } = {}) {
    const params = { account_id: accountId };
    if (before) params.before = before;
    if (after) params.after = after;
    return axios.get(`${this.url}/channels/${channelId}/messages`, { params });
  }

  // Com arquivos (#1756), o MESMO POST vai em multipart (`files[]`): mensagem e arquivos são
  // gravados juntos ou nada é gravado. `signal` cancela o envio no meio; `onUploadProgress` dá o
  // andamento.
  //
  // `replyToId` (#1775): a mensagem respondida, do mesmo canal — o servidor recusa (422) a de
  // qualquer outro lugar.
  sendMessage(
    accountId,
    channelId,
    content,
    { files = [], replyToId, onUploadProgress, signal } = {}
  ) {
    const url = `${this.url}/channels/${channelId}/messages`;
    if (!files.length) {
      const body = { account_id: accountId, content };
      if (replyToId) body.reply_to_id = replyToId;
      return axios.post(url, body);
    }
    const form = new FormData();
    form.append('account_id', accountId);
    form.append('content', content || '');
    if (replyToId) form.append('reply_to_id', replyToId);
    files.forEach(file => form.append('files[]', file, file.name));
    return axios.post(url, form, { onUploadProgress, signal });
  }

  // Encaminhar (#1775) para até 5 destinos da mesma conta: grupos/conversas (`channelIds`) e
  // pessoas (`userIds`, a conversa direta, criada se faltar). A resposta traz o resultado de
  // cada destino em `payload`.
  forwardMessage(
    accountId,
    channelId,
    messageId,
    { channelIds = [], userIds = [] }
  ) {
    return axios.post(
      `${this.url}/channels/${channelId}/messages/${messageId}/forward`,
      { account_id: accountId, channel_ids: channelIds, user_ids: userIds }
    );
  }

  // Uma mensagem (#1756): é como o front busca links novos para os arquivos dela — o evento do
  // socket não leva link, e o link vence (InternalChat::FileAccess).
  getMessage(accountId, channelId, messageId) {
    return axios.get(
      `${this.url}/channels/${channelId}/messages/${messageId}`,
      {
        params: { account_id: accountId },
      }
    );
  }

  // Editar e apagar a própria mensagem (#1757). Apagar é soft delete no servidor: a resposta é
  // a mensagem apagada, sem conteúdo.
  updateMessage(accountId, channelId, messageId, content) {
    return axios.patch(
      `${this.url}/channels/${channelId}/messages/${messageId}`,
      { account_id: accountId, content }
    );
  }

  deleteMessage(accountId, channelId, messageId) {
    return axios.delete(
      `${this.url}/channels/${channelId}/messages/${messageId}`,
      { params: { account_id: accountId } }
    );
  }

  // Grupos (#1749). Criar pede `internal_chat_manage` na conta; o resto da gestão é do
  // administrador DO GRUPO (#1764). Desfazer é arquivar (POST .../archive), nunca DELETE.
  createGroup(accountId, { name, memberIds }) {
    return axios.post(`${this.url}/groups`, {
      account_id: accountId,
      name,
      member_ids: memberIds,
    });
  }

  addGroupMembers(accountId, groupId, userIds) {
    return axios.post(`${this.url}/groups/${groupId}/members`, {
      account_id: accountId,
      user_ids: userIds,
    });
  }

  removeGroupMember(accountId, groupId, userId) {
    return axios.delete(`${this.url}/groups/${groupId}/members/${userId}`, {
      params: { account_id: accountId },
    });
  }

  // Promover (`admin`) ou rebaixar (`member`) um membro (#1764).
  setGroupMemberRole(accountId, groupId, userId, role) {
    return axios.patch(`${this.url}/groups/${groupId}/members/${userId}`, {
      account_id: accountId,
      role,
    });
  }

  // Nome e/ou regras do grupo (#1764). Só vai o que mudou.
  updateGroup(accountId, groupId, { name, settings } = {}) {
    const body = { account_id: accountId };
    if (name !== undefined) body.name = name;
    if (settings) body.settings = settings;
    return axios.patch(`${this.url}/groups/${groupId}`, body);
  }

  // Sair do grupo (#1764): qualquer membro. Resposta vazia (204).
  leaveGroup(accountId, groupId) {
    return axios.post(`${this.url}/groups/${groupId}/leave`, {
      account_id: accountId,
    });
  }

  archiveGroup(accountId, groupId) {
    return axios.post(`${this.url}/groups/${groupId}/archive`, {
      account_id: accountId,
    });
  }

  markRead(accountId, channelId, messageId) {
    const body = { account_id: accountId };
    if (messageId) body.message_id = messageId;
    return axios.post(`${this.url}/channels/${channelId}/read`, body);
  }
}

export default new InternalChatAPI();
